import { FAIcon, Theme, TagStyle, Tag, SearchDataStructure, AttributeTypes } from '../../../ui/components';
import { GetBulkActionOptionType, GetColumnsType } from '../../../ui/components/TableContainer/types/propsTypes';
import { DynamicObjectInterface } from '../../../sdk/type';
import { dateTimeToCustomFormat } from '../../../ui/utils';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import { QualifiedStatus, RewardStatus, Status } from '../../../ui/components/BulkActionModal/types/enums';

export interface MasterData {
  campaign: {
    campaignEnd?: string;
    campaignId?: string;
    campaignName?: string;
    campaignStart: string;
    recId?: number;
    status?: string;
  }[];
  reward?: {
    recId: number;
    rewardCode: string;
    rewardName: string;
  }[];
}
export interface CampaignOption {
  campaignEnd: string;
  campaignId: string;
  campaignName: string;
  campaignStart: string;
  status: string;
  title: string;
  value: string;
}

export interface BulkActionItem {
  id: string;
  title: string;
  value: string;
  icon: string;
}

export interface BulkActionOption {
  name: string;
  placeholder: string;
  dataset: DropDownData[];
}

export const dataSearchOption: SearchDataStructure[] = [
  {
    name: 'Participant Name',
    key: 'name',
    type: AttributeTypes.Textbox,
    visibility: true,
  },
  {
    name: 'Participant ABO ID',
    key: 'loginId',
    type: AttributeTypes.Textbox,
    visibility: true,
  },
  {
    name: 'Order Qualified',
    key: 'orderStatus',
    type: AttributeTypes.DropdownSingleSelect,
    visibility: true,
    dataset: [
      {
        key: 'yes',
        value: 'YES',
      },
      {
        key: 'no',
        value: 'NO',
      },
    ],
  },
  {
    name: 'Achieved Winning Criteria',
    key: 'isIndividualAchieved',
    type: AttributeTypes.DropdownSingleSelect,
    visibility: true,
    dataset: [
      {
        key: 'yes',
        value: 'YES',
      },
      {
        key: 'no',
        value: 'NO',
      },
      {
        key: 'pending',
        value: 'PENDING',
      },
    ],
  },
  {
    name: 'Participation Type',
    key: 'participationType',
    type: AttributeTypes.DropdownSingleSelect,
    visibility: true,
    dataset: [
      {
        key: 'team',
        value: 'Team',
      },
      {
        key: 'ind',
        value: 'Individual',
      },
    ],
  },
  {
    name: 'Reward Status',
    key: 'rewardStatus',
    type: AttributeTypes.DropdownSingleSelect,
    visibility: true,
    dataset: [
      {
        key: 'selected',
        value: 'Selected',
      },
      {
        key: 'allocated',
        value: 'Allocated',
      },
      {
        key: 'distributed',
        value: 'Distributed',
      },
    ],
  },
];

type IProductBO = {
  [key: string]: DynamicObjectInterface;
};

export const getParticipantListConfig: GetColumnsType = (
  styles: DynamicObjectInterface,
  translation: DynamicObjectInterface,
  cb: DynamicObjectInterface,
) => [
  {
    key: 'name',
    text: translation('admin.teamEdit.participantName'),
    width: 4,
    sortable: true,
    render: (object: IProductBO) => <span>{object.name === null ? '-' : object.name}</span>,
  },
  {
    key: 'loginId',
    text: translation('admin.teamEdit.participantAboId'),
    width: 4,
    sortable: true,
    render: (object: IProductBO) => <span>{object.loginId === null ? '-' : object.loginId}</span>,
  },
  {
    key: 'teamId',
    text: translation('admin.teams.teamId'),
    width: 4,
    sortable: true,
    render: (object: IProductBO) => (
      <span>{object.participationType === 'Individual' ? 'IND' : object.teamId === null ? '-' : object.teamId}</span>
    ),
  },
  {
    key: 'weightInDate',
    text: translation('admin.teamEdit.weighIn'),
    sortable: true,
    width: 4,
    render: (object: IProductBO) => (
      <span>{object.weightInDate === null ? '-' : dateTimeToCustomFormat(object.weightInDate, 'DD/MM/yyyy')}</span>
    ),
  },
  {
    key: 'weightOutDate',
    text: translation('admin.teamEdit.weighOut'),
    sortable: true,
    width: 4,
    render: (object: IProductBO) => (
      <span>{object.weightOutDate === null ? '-' : dateTimeToCustomFormat(object.weightOutDate, 'DD/MM/yyyy')}</span>
    ),
  },
  // {
  //   key: 'acknowledgeDateTimeOut',
  //   text: translation('admin.teamEdit.acknowledgedDateOut'),
  //   sortable: true,
  //   width: 4,
  //   render: (object: IProductBO) => (
  //     <span>
  //       {object.weightOut.acknowledgeDateTime === null
  //         ? '-'
  //         : dateTimeToCustomFormat(object.weightOut.acknowledgeDateTime, 'DD/MM/yyyy')}
  //     </span>
  //   ),
  // },
  {
    key: 'orderStatus',
    text: translation('admin.teamEdit.purchase'),
    sortable: true,
    width: 4,
    render: (object: IProductBO) => (
      <span>
        <Tag
          theme={object.orderStatus === 'YES' ? Theme.SUCCESS : Theme.WARNING}
          style={TagStyle.OUTLINE}
          text={object.orderStatus}
        />
      </span>
    ),
  },
  {
    key: 'isIndividualAchieved',
    text: translation('admin.teamEdit.achieved'),
    sortable: true,
    width: 4,
    render: (object: IProductBO) => (
      <Tag
        theme={
          object.isIndividualAchieved === 'YES'
            ? Theme.SUCCESS
            : object.isIndividualAchieved === 'NO'
              ? Theme.WARNING
              : Theme.BLUE
        }
        style={TagStyle.OUTLINE}
        text={object.isIndividualAchieved}
      />
    ),
  },
  {
    key: 'rewardStatus',
    text: translation('admin.teamEdit.rewardStatus'),
    sortable: true,
    width: 4,
    render: (object: IProductBO) => (
      <span>
        <Tag
          theme={
            object.rewardStatus === 'allocated'
              ? Theme.BLUE
              : object.rewardStatus === 'distributed'
                ? Theme.GREEN
                : Theme.GRAY
          }
          style={TagStyle.OUTLINE}
          text={(object.rewardStatus ?? '').toUpperCase()}
        />
      </span>
    ),
  },
  {
    key: 'status',
    text: translation('admin.teamEdit.status'),
    sortable: true,
    width: 2,
    render: (object: IProductBO) => (
      <span>
        <Tag
          theme={object.status === 'ACTIVE' ? Theme.SUCCESS : Theme.WARNING}
          style={TagStyle.OUTLINE}
          text={object.status}
        />
      </span>
    ),
  },
  {
    key: 'actions',
    width: 1,
    text: translation('admin.teamEdit.actions'),
    render: (object: IProductBO) => (
      <>
        <FAIcon
          iconName={'edit'}
          onClick={e => {
            cb?.onEdit(object);
            e.stopPropagation();
          }}
          theme={Theme.PURPLE}
        />
      </>
    ),
  },
];

export const bulkActionOption: GetBulkActionOptionType = (cb: DynamicObjectInterface) => {
  return {
    name: 'participant-bulk-action',
    placeholder: 'Actions',
    dataset: [
      {
        id: 'Edit',
        title: 'Edit',
        value: 'Edit',
        icon: 'pen-to-square',
        onClick: (e: DynamicObjectInterface) => {
          cb?.onBulkEdit();
        },
      },
      {
        id: 'Delete',
        title: 'Delete',
        value: 'Delete',
        icon: 'trash-can',
        onClick: (e: DynamicObjectInterface) => {
          cb?.onBulkDelete();
        },
      },
    ],
  };
};

export const bulkActionEditConfig: DynamicObjectInterface = (translation: DynamicObjectInterface) => [
  {
    name: 'status',
    label: 'Status',
    type: 'checkboxRadio',
    options: [
      { label: translation('Active'), value: Status.ACTIVE },
      { label: translation('Inactive'), value: Status.INACTIVE },
    ],
  },
  // {
  //   name: 'preAssessmentDate',
  //   label: 'Pre-Assessment Date',
  //   type: 'checkboxDate'
  // },
  // {
  //   name: 'postAssessmentDate',
  //   label: 'Post-Assessment Date',
  //   type: 'checkboxDate'
  // },
  {
    name: 'orderStatus',
    label: 'Order Qualified',
    type: 'checkboxRadio',
    options: [
      { label: translation('Yes'), value: QualifiedStatus.YES },
      { label: translation('No'), value: QualifiedStatus.NO },
    ],
  },
  // {
  //   name: 'isIndividualAchieved',
  //   label: 'Winning Criteria Achieved',
  //   type: 'checkboxRadio',
  //   options: [
  //     { label: translation('Yes'), value: QualifiedStatus.YES },
  //     { label: translation('No'), value: QualifiedStatus.NO }
  //   ]
  // },
  {
    name: 'rewardStatus',
    label: 'Reward Status',
    type: 'checkboxRadio',
    optionsLayout: 'block',
    options: [
      { label: 'Selected', value: RewardStatus.SELECTED },
      { label: 'Allocated', value: RewardStatus.ALLOCATED },
      { label: 'Distributed', value: RewardStatus.DISTRIBUTED },
    ],
  },
];
